import styled from 'styled-components';
import { OpeningHours } from '../OpeningHours';
import { LinkButton } from 'components/LinkButton';
import { InfoIcon, PhoneIcon, StoreIcon } from 'svg';
import { colors, icons } from '../../constants';
import { useGoogleMapsContext } from 'context';
import { IAvailableStore } from '../../types';
import { dataLayerStoreEvent } from 'utils';

const TitleContainer = styled.div``;
const Title = styled.h4``;
const Distance = styled.span``;
const Address = styled.div``;
const ButtonContainer = styled.div``;
const StyledLink = styled.a``;
const StoreNote = styled.div``;

interface IProps {
    className?: string;
    hideDistance?: boolean;
    hideOpeningHours?: boolean;
    store: IAvailableStore;
    searchPosition: number;
    isInfoWindow?: boolean;
}

const BaseStoreCard = ({
    className,
    hideDistance,
    hideOpeningHours,
    searchPosition,
    isInfoWindow,
    store,
    store: {
        address,
        distanceKm,
        id: storeId,
        storeUrl,
        storeName,
        storeNote,
        specialHours,
        openingHours,
        phone,
        city,
        storeAppointmentUrl,
    },
}: IProps) => {
    const { googleMapsRef } = useGoogleMapsContext();

    const setMarkerIcon = (storeId: string, iconUrl: string) => {
        const match = googleMapsRef.current.markers?.find(
            (m) => m.store.id === storeId
        );

        if (match) {
            match.marker.setIcon({ url: iconUrl });
        }
    };

    const handleMouseEnter = () => setMarkerIcon(storeId, icons.markerHover);
    const handleMouseLeave = () => setMarkerIcon(storeId, icons.marker);

    const isSpecialStoreNote =
        storeNote ===
        'We hebben tijdelijk aangepaste openingstijden. Bekijk ze hieronder.';
    const hideStoreNote = isSpecialStoreNote && !specialHours.length;
    const showStoreNote = storeNote && !hideStoreNote && !hideOpeningHours;

    const eventNameAppointment = isInfoWindow
        ? 'map click - book appointment'
        : 'book appointment click';
    const eventNameDetails = isInfoWindow
        ? 'map click - store details'
        : 'store details click';

    const dataLayerEvent = (interaction: string) =>
        dataLayerStoreEvent(store, searchPosition, interaction);

    return (
        <div
            className={className}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <TitleContainer>
                <Title>{storeName}</Title>
                {!!distanceKm && !hideDistance && (
                    <Distance>{`${distanceKm.toFixed(
                        1
                    )} km bij je vandaan`}</Distance>
                )}
            </TitleContainer>
            <Address>
                {address}, {city}
            </Address>
            <ButtonContainer>
                <StyledLink href={`tel:${phone}`}>
                    <PhoneIcon />
                    {phone}
                </StyledLink>
                <StyledLink
                    href={storeUrl}
                    onClick={() => dataLayerEvent(eventNameDetails)}
                >
                    <StoreIcon />
                    Bekijk de details
                </StyledLink>
            </ButtonContainer>
            <OpeningHours
                hideOpeningHours={hideOpeningHours}
                openingHours={openingHours}
                specialHours={specialHours}
                onOpeningHoursClick={() =>
                    !isInfoWindow && dataLayerEvent('opening times click')
                }
            />
            {showStoreNote && (
                <StoreNote>
                    <InfoIcon />
                    <span>{storeNote}</span>
                </StoreNote>
            )}
            <LinkButton
                href={storeAppointmentUrl}
                title="Maak een afspraak"
                onClick={() => dataLayerEvent(eventNameAppointment)}
            />
        </div>
    );
};

export const StoreCard = styled(BaseStoreCard)`
    border-radius: 5px;
    padding: 1rem;
    color: ${colors.black};
    font-size: 16px;
    transition: border 0.2s ease-in-out;
    border: 1px solid ${colors.greyBorder};

    ${TitleContainer} {
        column-gap: 1rem;
        display: flex;
        justify-content: space-between;
    }

    ${Title} {
        font-size: 1.125rem;
        font-weight: 500;
        line-height: 1.5;
    }

    ${Distance} {
        font-size: 0.875rem;
        line-height: 2;
        white-space: nowrap;
        color: ${colors.greyDark};
    }

    ${Address} {
        font-size: 0.875rem;
        line-height: 1.71;
        margin-bottom: 1rem;
        color: ${colors.greyDark};
    }

    ${ButtonContainer} {
        line-height: 1.71;
        margin-bottom: 1.4rem;
    }

    ${StyledLink} {
        align-items: center;
        display: flex;
        gap: 0.8rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.71;
        margin-bottom: 4px;
        color: ${colors.orange};

        &:hover {
            text-decoration: underline;
        }
    }

    ${StoreNote} {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        font-size: 0.875rem;
        line-height: 1.71;
        color: ${colors.blueDark};
        background-color: ${colors.infoAlert};
        border: 1px solid ${colors.blueDark};
        border-radius: 5px;
        padding: 16px;
        margin: 1rem 0;
        width: 100%;
    }

    ${InfoIcon} {
        fill: ${colors.blueDark};
        height: 24px;
        width: 24px;
        flex-shrink: 0;
    }

    ${PhoneIcon}, ${StoreIcon} {
        height: 1rem;
        width: 1rem;
    }

    ${LinkButton} {
        margin-top: 1.4rem;
        width: 100%;
    }
`;
